import { useState, useEffect } from "react";
import URLs from "constants/urls";

export const useAccountsApi = (resetFormState) => {
  const [accounts, setAccounts] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [loading, setLoading] = useState(false);
  const [voucherData, setVoucherData] = useState({});

  const showAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleCloseAlert = () => {
    setOpen(false);
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [accountsResponse, companiesResponse, paymentAccountsResponse] = await Promise.all([
        fetch(URLs.GetCashBankAccounts),
        fetch(URLs.GetCompanies),
        fetch(URLs.GetPaymentAccounts),
      ]);

      if (!accountsResponse.ok)
        throw new Error(`Failed to fetch accounts: ${accountsResponse.statusText}`);
      if (!companiesResponse.ok)
        throw new Error(`Failed to fetch companies: ${companiesResponse.statusText}`);
      if (!paymentAccountsResponse.ok)
        throw new Error(`Failed to fetch payment accounts: ${paymentAccountsResponse.statusText}`);

      const accountsData = await accountsResponse.json();
      const companiesData = await companiesResponse.json();
      const paymentAccountsData = await paymentAccountsResponse.json();

      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);

      // ✅ Merge and deduplicate by accountID to avoid duplicate key warnings
      const merged = [
        ...(Array.isArray(paymentAccountsData) ? paymentAccountsData : []),
        ...(Array.isArray(accountsData) ? accountsData : []),
      ];
      const unique = merged.filter(
        (item, index, self) => index === self.findIndex((t) => t.accountID === item.accountID)
      );
      setPaymentAccounts(unique);
    } catch (error) {
      showAlert("Error fetching data: " + error.message, "error");
      setAccounts([]);
      setCompanies([]);
      setPaymentAccounts([]);
    }
    setLoading(false);
  };

  const manageVouchersApi = async (payload, fromDate, toDate, Company, seachTypeForGet) => {
    setLoading(true);
    try {
      const response = await fetch(URLs.PostVoucherEntry, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to post voucher entry: ${errorText}`);
      }

      const result = await response.json();

      if (payload.RequestType === "ADD") {
        if (result.PrintFormat) {
          const newWindow = window.open("", "_blank");
          newWindow.document.open();
          newWindow.document.write(result.PrintFormat);
          newWindow.document.close();
        }
      } else {
        showAlert("Voucher Updated", "success");
      }

      const pyload = {
        companyId: Company,
        fromDate,
        toDate,
        searchType: seachTypeForGet,
      };
      resetFormState();
      fetchListOfVouchers(pyload);
    } catch (error) {
      showAlert(error.message, "error");
    } finally {
      setIsEditMode(false);
      setLoading(false);
    }
  };

  // ✅ searchType param added and passed down to deleteVoucher
  const handleDeleteVoucher = async (voucherID, cid, fromDate, toDate, Company, searchType) => {
    setLoading(true);
    const payload = { companyId: cid, voucherID };
    try {
      const response = await fetch(URLs.GetJVItem, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to fetch voucher details: ${response.statusText}`);
      const data = await response.json();
      await deleteVoucher(data, fromDate, toDate, Company, searchType);
    } catch (error) {
      showAlert("Error fetching voucher details before deletion: " + error.message, "error");
    }
    setLoading(false);
  };

  // ✅ searchType param used instead of hardcoded "PMT"
  const deleteVoucher = async (data, fromDate, toDate, Company, searchType) => {
    const payload = { ...data, RequestType: "DELETE" };
    setLoading(true);
    try {
      const response = await fetch(URLs.PostVoucherEntry, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        showAlert(`Failed to delete voucher: ${response.statusText}`, "error");
        return;
      }
      showAlert("Voucher deleted successfully.", "success");
      const pyload = {
        companyId: Company,
        fromDate,
        toDate,
        searchType: searchType, // ✅ fixed — no longer hardcoded "PMT"
      };
      fetchListOfVouchers(pyload);
    } catch (error) {
      showAlert("Error deleting voucher: " + error.message, "error");
    }
    setLoading(false);
  };

  const fetchListOfVouchers = async (payload) => {
    setLoading(true);
    try {
      const response = await fetch(URLs.GetVoucherEntryList, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to fetch vouchers: ${response.statusText}`);

      const data = await response.json();
      const formattedData = data.map((item) => ({
        Date: item.entryDate,
        VoucherNo: item.voucherClientID,
        Particulars: item.particulars,
        Amount: item.amount,
        voucherID: item.voucherID,
        companyId: item.companyId,
      }));
      setTableData(formattedData);
    } catch (error) {
      showAlert("Error fetching vouchers: " + error.message, "error");
    }
    setLoading(false);
  };

  const getVoucherApi = async (payload) => {
    setLoading(true);
    try {
      const response = await fetch(URLs.GetJVItem, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Failed to fetch voucher details: ${response.statusText}`);

      const data = await response.json();
      setVoucherData(data);
      return data;
    } catch (error) {
      showAlert("Error fetching voucher details: " + error.message, "error");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  return {
    companies,
    accounts,
    paymentAccounts,
    tableData,
    voucherData,
    isEditMode,
    open,
    setOpen,
    showAlert,
    message,
    alertType,
    loading,
    setLoading,
    handleDeleteVoucher,
    getVoucherApi,
    setIsEditMode,
    fetchListOfVouchers,
    manageVouchersApi,
    handleCloseAlert,
  };
};
